// THE ONE PRODUCTION CALL SITE THIS SEVERANCE MOVES (ruling 40 §6).
//
// These two names used to come from `@/atoms/clinicalTasksAtoms`, which is a
// re-export shim over the `@heidi/tasks` ROOT door — so this module, which
// `pages/_app` reaches through `authModel`, pulled twenty rendered components
// and seventeen design-system doors in order to read a string prefix and build
// a scope. `sideEffects: false` made that free in the bundle and changed
// nothing about the graph; the programme owner ruled that tree-shaking is not
// an architectural boundary, so the vocabulary moved to the INERT
// `@heidi/tasks/contracts` door and this import follows it there.
//
// `buildClinicalTasksStorageScope` takes the identity rather than reading it,
// because `/contracts` may not import `@heidi/workspace/data`. The two readers
// below are the same bindings the package calls — the workspace one straight
// off that door, the current-user one through an app re-export of it — so the
// scope this file computes is byte-identical
// to the one the atom module computes, and `logoutStorage.test.ts` asserts
// exactly that by still resolving its expected scope through the atom module.
import {
  buildClinicalTasksStorageScope,
  CLINICAL_TASKS_STORAGE_KEY_PREFIX,
} from '@heidi/tasks/contracts';

import {
  AMBIENT_HEIDI_ENABLED_STORAGE_KEY,
  ambientHeidiEnabledAtom,
} from '@/app-shell/ambient-heidi-preference';
import { clearTrackYourRemoteLastOrderIds } from '@/app-shell/chronicle/device-settings/track-your-remote/utils/last-order-id-storage';
import {
  AGENT_HOME_PANEL_COLLAPSED_KEY,
  clearAgentHomePanelPreference,
} from '@/app-shell/layout/Navigation/nav-v3/agent-home-panel-state';
import { clearMagicFlowPendingDeepLink } from '@/app-shell/rpa/onboarding/magic-flow-pending-deep-link';
import { NARRATION_PREFERENCE_STORAGE_KEY } from '@/app-shell/rpa/recorder/model/recording-narration-preference';
import { LAST_SETTINGS_ROUTE_STORAGE_KEY } from '@/app-shell/settings/atoms/settingsModalAtom';
import { removeAllChatDraftsFromStorage } from '@/atoms/chatDraftStorage';
import { EVIDENCE_POWER_USER_BANNER_DISMISSED_STORAGE_KEY } from '@/atoms/evidenceTrialQueryCountAtom';
import {
  CACHED_LAST_USED_RECORDER_SETTINGS_STORAGE_KEY,
  clearCachedLastUsedRecorderSettings,
} from '@/atoms/offline-recording/cached-recorder-settings';
import { clearOfflinePatientLinks } from '@/atoms/offline-recording/patient-link';
import { clearPendingConsent } from '@/atoms/offline-recording/pending-consent';
import { clearPendingOfflinePatientLinkWrites } from '@/atoms/offline-recording/pending-patient-link-writes';
import { clearPendingTranscribeMode } from '@/atoms/offline-recording/pending-transcribe-mode';
import {
  clearPendingUserDefaultTranscribeMode,
  PENDING_USER_DEFAULT_TRANSCRIBE_MODE_STORAGE_KEY,
} from '@/atoms/offline-recording/pending-user-default-transcribe-mode';
import { SOURCE_CONTROL_TOGGLE_STORAGE_KEY } from '@/atoms/sourceControlAtoms';
import { TEMPLATE_PICKER_SORT_FILTER_KEY } from '@/components/ui/choose-note-structure-modal/choose-note-structure-command/atoms';
import { ABANDONED_TRACKINGS_STORAGE_KEY } from '@/features/performanceTracking';
import {
  REFILL_BATCH_RUN_STORAGE_KEY_PREFIX,
  ROUTINES_DESKTOP_NURTURE_BANNER_DISMISSED_STORAGE_KEY,
  ROUTINES_EXPLORE_BANNER_DISMISSED_STORAGE_KEY,
} from '@heidi/agents/contracts';
import { PROJECTS_BACKGROUND_REGISTRY_KEY } from '@heidi/agents/data';
import { DICTATION_FEEDBACK_INCREASED_CADENCE_PROMPT_COUNT_STORAGE_KEY } from '@heidi/dictate/contracts';
import {
  clearDictateDownloadEntry,
  DEFAULT_DICTAPHONE_TRIGGER_MODE,
  DEVICE_BUTTON_MAPPINGS_STORAGE_KEY,
  deviceButtonMappingsAtom,
  DICTAPHONE_TRIGGER_MODE_STORAGE_KEY,
  dictaphoneTriggerModeAtom,
} from '@heidi/dictate/data';
import {
  EVIDENCE_COPY_CONFIRMED_CHAT_IDS_STORAGE_KEY,
  EVIDENCE_QUERY_HANDOFF_STORAGE_KEY,
  SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY,
} from '@heidi/evidence/contracts';
import { clearPracticeInviteSessionState } from '@heidi/growth/data';
import {
  clearEhrLaunchRegion,
  clearIntegrationAuthOnLogout,
  EHR_LAUNCH_REGION_STORAGE_KEY,
} from '@heidi/integrations/ehr/data/atoms';
import {
  CACHED_VISIBLE_SCOPES_STORAGE_KEY,
  DESKTOP_SESSIONS_LIST_CACHE_KEY,
  SESSION_LIST_COLUMN_VISIBILITY_STORAGE_KEY,
  SESSION_LIST_SCOPE_STORAGE_KEY,
  SESSION_LIST_SPLIT_COLUMN_ORDER_STORAGE_KEY,
  SESSION_LIST_SPLIT_COLUMN_VISIBILITY_STORAGE_KEY,
  SESSION_LIST_VIEW_BY_SCOPE_STORAGE_KEY,
} from '@heidi/session/contracts';
import {
  clearPendingTeamJoinNote,
  consumeTeamJoinBootNote,
  getCurrentUser,
  getWorkspaceId,
  setWorkspaceId,
  USER_SESSION_TIMEOUT_STORAGE_KEY,
  WORKSPACE_ID_STORAGE_KEY,
} from '@heidi/workspace/data';
// The leaf-safe current-user read. Deliberately NOT `@/hooks/query/useQueryUser`:
// this module sits on the GRO-3989 cycle (`logoutStorage -> session-timeout-state
// -> useQueryUser -> authActions -> logoutStorage`) and `current-user-cache`
// exists precisely so a leaf can read the user without pulling that graph.
import {
  clearUserScopedKeysForScope,
  CURRENT_USER_REF_ID_STORAGE_KEY,
  getUserStorageScope,
} from '@/lib/jotai-storage/user-scoped';
import { setDeviceButtonMappings, setHardwareTriggerMode } from '@/tauri/dictation/api';
import { setActiveWorkspace } from '@/tauri/workspace';
import { logger } from '@/utils/logger/logger';
import { sanitizeError } from '@/utils/logger/sanitizeError';
import { getDefaultStore } from 'jotai';
const LOGOUT_KEYS = [
  WORKSPACE_ID_STORAGE_KEY,
  EVIDENCE_POWER_USER_BANNER_DISMISSED_STORAGE_KEY,
  ROUTINES_DESKTOP_NURTURE_BANNER_DISMISSED_STORAGE_KEY,
  ROUTINES_EXPLORE_BANNER_DISMISSED_STORAGE_KEY,
  // EVI-1324: source-control selections are persisted to the BE and warm-cached
  // in localStorage via `atomWithStorage`. Drop the cache on full logout so a
  // shared browser doesn't leak the previous user's selections to the next one
  // before their BE state hydrates.
  SOURCE_CONTROL_TOGGLE_STORAGE_KEY,
  // The server default this browser applied for a user with nothing saved.
  SOURCE_CONTROL_APPLIED_SERVER_DEFAULT_STORAGE_KEY,
  EVIDENCE_COPY_CONFIRMED_CHAT_IDS_STORAGE_KEY,
  CACHED_VISIBLE_SCOPES_STORAGE_KEY,
  ABANDONED_TRACKINGS_STORAGE_KEY,
  // Narration is sticky for one clinician, but must reset on full logout so a
  // shared workstation never starts the next clinician with mic capture opted in.
  NARRATION_PREFERENCE_STORAGE_KEY,
  // Same rationale as source-control above: the settings modal remembers the
  // user's last-viewed section in `atomWithStorage`. On a shared browser this
  // would otherwise leak the previous user's last section (e.g. `Billing`,
  // `Blocked Numbers`) to whoever signs in next. The app's other forced-
  // logout paths (session-inactivity timeout, 401 expiry on Auth0/Kinde,
  // desktop session expiry) all funnel through here too, so dropping the key
  // on logout is the full lifecycle for this preference.
  LAST_SETTINGS_ROUTE_STORAGE_KEY,
  // The desktop offline-relaunch sessions-list seed cache persists the first
  // session page — including `patient_profile_name` PHI — to unscoped
  // localStorage. Clear it on logout so a shared desktop workstation can't
  // hydrate the next clinician's list with the previous user's patient names.
  DESKTOP_SESSIONS_LIST_CACHE_KEY,
  // The projects background registry (AGE-927) persists run pointers —
  // including `projectTitle`, which can carry clinical context — to unscoped
  // localStorage so a run survives navigation/restart. Clear it on logout so a
  // shared clinician device can't leak the previous user's project titles (or
  // any free-text a later stacked PR persists) to the next sign-in.
  PROJECTS_BACKGROUND_REGISTRY_KEY,
  // The cached current-user id that namespaces the new session list's per-user
  // view/column storage keys. Clear it on logout so the next user on a shared
  // device can't resolve — and read — the previous user's scoped cache.
  CURRENT_USER_REF_ID_STORAGE_KEY,
  // Same rationale as the settings-route key above: the template picker now
  // persists the chosen sort order in `atomWithStorage`. Before it was in
  // memory, so every login started at the default grouped view; persistence
  // removes that guarantee across users too, so on a shared clinical
  // workstation clinician A's sort order would otherwise apply to clinician B.
  // Drop it on full logout to restore the default for the next sign-in.
  TEMPLATE_PICKER_SORT_FILTER_KEY,
  // The two offline-recording preferences below are ALSO cleared by the calls
  // in `clearLogoutStorage`, and those calls are the primary mechanism — each
  // empties the live atom and then removes the row. These rows are the registry
  // entry every persisted user-derived key is required to have, and a backstop
  // if a call site is ever dropped.
  //
  // A row here on its own is NOT coverage, which is the defect this fixes: the
  // loop removes from localStorage and never touches the atom, and both of
  // these are read through non-reactive getters that read the ATOM.
  //
  // The Transcribe/Dictate mode a clinician picked while offline, held until
  // the reconnect replay can PATCH it to /user. It is an overlay that
  // deliberately outranks the live user object, so it does not self-heal on a
  // user switch: left behind on a shared workstation it would drive clinician
  // B's offline sessions AND be written onto B's account when connectivity
  // returns.
  PENDING_USER_DEFAULT_TRANSCRIBE_MODE_STORAGE_KEY,
  // The last-used recorder snapshot (template, transcribe mode, languages,
  // voice style) that `buildOfflineCreateArgs` pre-fills an offline session
  // from. It used to be excused with "the next clinician's /user response
  // overwrites it" — an assumption that a network response arrives, inside the
  // offline feature. On a cold offline boot after a user switch, none does.
  CACHED_LAST_USED_RECORDER_SETTINGS_STORAGE_KEY,
  // Agent Home panel collapse preference is atom-backed; the row is the registry
  // entry and backstop — `clearAgentHomePanelPreference` owns both halves.
  AGENT_HOME_PANEL_COLLAPSED_KEY,
  // The region an EHR / SMART-on-FHIR launch named, which took over from the
  // `auth.userRegion` slice field when that stopped being persisted. Same shape
  // as the two offline preferences above: atom-backed, read through a
  // non-reactive getter that reads the ATOM, so `clearEhrLaunchRegion` is the
  // mechanism and this row is the registry entry and the backstop.
  //
  // It is not a preference, though — it steers which data plane clinical coding
  // and dictation talk to, and it is read ABOVE both `userRegion` and
  // `kindeRegion`, so nothing downstream can correct it. Left behind on a shared
  // workstation, one non-AU launch pins the region for every later sign-in.
  EHR_LAUNCH_REGION_STORAGE_KEY,
  // Per-device Ambient Heidi opt-in, cleared deliberately: a clinician re-enables
  // "Hey Heidi" after every sign-out, including session expiry, so an always-on
  // mic never carries over on a shared workstation. Reset the live atom too so a
  // sign-out that does not reload cannot leave it on.
  AMBIENT_HEIDI_ENABLED_STORAGE_KEY,
] as const;

/**
 * User-scoped base keys deliberately kept across logout, so a returning
 * clinician gets their session list back (see each key's own doc comment).
 * Everything else user-scoped is swept, so a new scoped atom is covered by
 * default. The trade: these outlive their user on a shared workstation,
 * and nothing evicts them yet.
 *
 * Because they survive, a key only belongs here if it can never hold patient
 * data — the persisted view state deliberately excludes the patient filter for
 * this reason. Anything carrying clinical context must be swept instead.
 */
export const USER_SCOPED_KEYS_KEPT_ACROSS_LOGOUT = [
  SESSION_LIST_SCOPE_STORAGE_KEY,
  SESSION_LIST_VIEW_BY_SCOPE_STORAGE_KEY,
  SESSION_LIST_COLUMN_VISIBILITY_STORAGE_KEY,
  SESSION_LIST_SPLIT_COLUMN_VISIBILITY_STORAGE_KEY,
  SESSION_LIST_SPLIT_COLUMN_ORDER_STORAGE_KEY,
  // Session inactivity timeout preference (minutes + source only — no PHI).
  // Kept so a clinician's opt-in survives auto-logout and re-login when the
  // BE cohort resolver still returns null minutes on GET /user.
  USER_SESSION_TIMEOUT_STORAGE_KEY,
  // Mic button → function or snippet id. Snippet ids are not patient data.
  // Kept so a returning clinician does not reconfigure the mic; the user suffix
  // stops the next clinician reading or re-syncing it.
  DEVICE_BUTTON_MAPPINGS_STORAGE_KEY,
  // Dictaphone double- vs single-press hands-free choice. No patient data;
  // kept for the same reason as the button mappings.
  DICTAPHONE_TRIGGER_MODE_STORAGE_KEY,
  // Feedback cadence contains no patient data and remains isolated by user id.
  DICTATION_FEEDBACK_INCREASED_CADENCE_PROMPT_COUNT_STORAGE_KEY,
] as const;

function clearScopedUserPrefs() {
  const scope = getUserStorageScope();
  if (!scope) {
    return;
  }
  clearUserScopedKeysForScope(scope, USER_SCOPED_KEYS_KEPT_ACROSS_LOGOUT);
}

function clearScopedClinicalTasksPrefs() {
  const scope = buildClinicalTasksStorageScope({
    workspaceId: getWorkspaceId(),
    userId: getCurrentUser()?.kinde_user_id,
  });
  if (!scope) {
    return;
  }
  const suffix = `:${scope}`;
  try {
    // Prefix + scope scan (rather than enumerating each preference key) so a
    // new clinical-tasks preference key can't be added to
    // `clinicalTasksAtoms.ts` without also being cleared here.
    Object.keys(localStorage)
      .filter(
        (key) => key.startsWith(CLINICAL_TASKS_STORAGE_KEY_PREFIX) && key.endsWith(suffix)
      )
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore — restricted webviews may block storage access.
  }
}

/**
 * Clears the departing clinician's batch-sign run, plus any unscoped row left
 * by a build that predates the scoping.
 *
 * A prefix scan rather than one computed key, for the legacy row: before
 * AGE-2370's review fix the record was written to the bare prefix, and that
 * row would otherwise sit there offering "Sign the remaining 7" to whoever
 * signs in next. Scoped rows belonging to OTHER accounts are left alone —
 * clearing them would drop a run a different clinician can still resume.
 */
function clearRefillBatchRun() {
  const scope = buildClinicalTasksStorageScope({
    workspaceId: getWorkspaceId(),
    userId: getCurrentUser()?.kinde_user_id,
  });
  const mine = scope ? `${REFILL_BATCH_RUN_STORAGE_KEY_PREFIX}:${scope}` : null;
  try {
    Object.keys(localStorage)
      .filter(
        (key) =>
          key === REFILL_BATCH_RUN_STORAGE_KEY_PREFIX || (mine !== null && key === mine)
      )
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // Ignore — restricted webviews may block storage access.
  }
}

/**
 * Removes localStorage (and practice-invite sessionStorage) entries that must
 * not survive a full logout.
 *
 * Called from both `authModel.logout` and `authModel.logoutWithoutRedirect`.
 * Not called during stale-session cleanup (`clearPersistedModels`) because the
 * user will re-authenticate into the same workspace.
 *
 * `logoutWithoutRedirect` therefore now performs the integration-auth reset
 * too. It did not before: that state lived in the redux-persist blob and was
 * reset by a dispatch on the `logout` path only, so the two sign-out paths
 * disagreed about state neither of them could see from here.
 */
export function clearLogoutStorage() {
  // Both sweeps resolve their scope from a key that `LOGOUT_KEYS` clears below,
  // so they must run first.
  clearScopedUserPrefs();
  clearScopedClinicalTasksPrefs();
  // Same ordering reason as the two sweeps above: it resolves its scope from
  // the workspace id that `setWorkspaceId(null)` below clears.
  clearRefillBatchRun();
  clearPracticeInviteSessionState();
  // Integration-auth state is atom-backed, so its keys are swept by writing
  // the atoms rather than by removing the keys — removing them behind a live
  // atom would leave memory and storage disagreeing until the next reload.
  // The six launch-identity fields deliberately survive, so the widget can
  // sign the next user in against the same host-EHR launch.
  clearIntegrationAuthOnLogout();
  // Rust-side mirror of the workspace id must not survive into the next
  // account's session (desktop only; no-op on web).
  setActiveWorkspace(null).catch((error) => {
    logger.warn({
      msg: '[workspace_header] Failed to clear active workspace in Rust on logout',
      error: error instanceof Error ? error.message : String(error),
    });
  });
  // Removing the key is not enough on the sign-out that never reloads
  // (`logoutWithoutRedirect`): the live atom would survive into the next
  // account. Must run after the scope sweeps that read it and before the
  // removal loop, since writing through the atom re-persists the key.
  setWorkspaceId(null);
  // Same reason as the line above, for two stores that have no key at all: the
  // deferred offline replay maps are in memory only, so a sign-out that never
  // reloads carries them into the next account and the reconnect drain would
  // drive the next clinician's token against the previous clinician's session
  // ids. Consent is the one that matters — it is a compliance record — but the
  // transcribe-mode store it was modelled on has the identical exposure.
  clearPendingConsent();
  clearPendingTranscribeMode();
  // A desktop Magic Flow link held for sign-in belongs to whoever received it.
  clearMagicFlowPendingDeepLink();
  // Same reason as the line above, for a key the loop below could never reach:
  // the offline patient-link drafts are patient identifiers the clinician typed
  // while offline, and they live in sessionStorage — the loop removes from
  // localStorage only. sessionStorage outlives every sign-out branch, including
  // the ones that navigate, so without this the next account's session header
  // and `offline_recording_create` would carry the previous clinician's typed
  // identifiers. Clears the live atom as well as the row; see the call for why
  // one without the other is a half-fix.
  clearOfflinePatientLinks();
  // The onboarding records live in sessionStorage too; left behind, the next
  // account in this tab resumes the previous clinician's onboarding, or has
  // the previous visitor's invite-link answer applied to its own invite.
  try {
    sessionStorage.removeItem('heidi-onboarding:personalization-progress:v1');
    sessionStorage.removeItem('heidi-onboarding:auth-onboard-answers:v1');
    sessionStorage.removeItem('heidi-onboarding:invite-link-answer:v1');
    // The pending Evidence question handed from the public `/chat` page to
    // `/scribe/chat` (ARCFE-1182) is clinical text in sessionStorage. Left
    // behind, the next account in this tab would auto-send the previous
    // clinician's question as its own first message.
    sessionStorage.removeItem(EVIDENCE_QUERY_HANDOFF_STORAGE_KEY);
  } catch {
    // Storage access can be denied; the keys below still get their turn.
  }
  // The other half of the line above: the identifiers whose offline write could
  // not land yet, held in memory per session id until the sync-queue mirror
  // names a local row. Clearing the drafts and leaving these would mean the
  // next mount of one of those session routes re-issues the departing
  // clinician's typed identifier under the new account's token.
  clearPendingOfflinePatientLinkWrites();
  // The Dictate-entry marker is tab storage; left set, the next account in this
  // tab never sees the Heidi II welcome.
  clearDictateDownloadEntry();
  // The accepted-invite note is tab storage too: left behind, the next account
  // would be checked for membership of a team it never asked to join.
  clearPendingTeamJoinNote();
  consumeTeamJoinBootNote();
  // Same reason as the line above, for two offline-recording preferences that
  // the loop below could only ever half-clear. Both are live storage atoms, and
  // both are read through non-reactive getters that read the ATOM rather than
  // storage — so removing the row leaves the departing clinician's value in
  // memory, driving the next account's offline sessions and re-persisting
  // itself on the next write.
  //
  // The transcribe mode already had a `LOGOUT_KEYS` row and that row was the
  // half-fix: it removed the key and never touched the overlay the reconnect
  // replay then PATCHes onto the next clinician's account. The snapshot had no
  // row at all. Each clear owns both halves in order; the rows stay in the list
  // as the registry entry and the backstop, not as the mechanism.
  clearPendingUserDefaultTranscribeMode();
  clearCachedLastUsedRecorderSettings();
  clearAgentHomePanelPreference();
  // The EHR-launch region, for the same both-halves reason and one more: it
  // replaced a field on the auth slice that `store.dispatch.auth.clear()` wiped
  // on every sign-out, so its lifetime has to match that field's or the swap
  // was not behaviour-preserving. `clearPersistedModels` clears it on the third
  // path, beside its own `auth.clear()`.
  clearEhrLaunchRegion();
  // Unsent chat drafts are clinician-typed text in unscoped storage, one key
  // per chat. The sweep also empties any mounted draft atom.
  removeAllChatDraftsFromStorage();
  // Track-your-Remote last Order ID is user-scoped but still localStorage
  // PHI-adjacent buyer data — prefix-scan so a shared desktop cannot leak it.
  clearTrackYourRemoteLastOrderIds();
  LOGOUT_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      // Ignore — restricted webviews may block storage access.
    }
  });
  // After the user id is gone, so these writes cannot persist defaults over
  // the kept per-user blobs. The live atoms and the native reader both outlive
  // a sign-out that does not reload.
  const store = getDefaultStore();
  store.set(ambientHeidiEnabledAtom, false);
  store.set(deviceButtonMappingsAtom, {});
  store.set(dictaphoneTriggerModeAtom, DEFAULT_DICTAPHONE_TRIGGER_MODE);
  void Promise.resolve(setDeviceButtonMappings({})).catch((error: unknown) => {
    logger.warn({
      msg: 'Failed to clear device button mappings on logout',
      error: sanitizeError(error),
    });
  });
  void Promise.resolve(setHardwareTriggerMode(DEFAULT_DICTAPHONE_TRIGGER_MODE)).catch(
    (error: unknown) => {
      logger.warn({
        msg: 'Failed to reset dictaphone trigger mode on logout',
        error: sanitizeError(error),
      });
    }
  );
}
